/**
 * Mobile touch controls: virtual joystick + action buttons
 * Auto-detected based on touch support + screen size
 */

export class TouchControls {
  private container: HTMLElement;
  private canvas: HTMLCanvasElement;

  // Virtual joystick state
  private joystickActive = false;
  private joystickId: number | null = null;
  private joystickOrigin = { x: 0, y: 0 };
  private joystickPos = { x: 0, y: 0 };
  private readonly JOYSTICK_RADIUS = 50;

  // Look (right side drag)
  private lookId: number | null = null;
  private lookLastPos = { x: 0, y: 0 };
  lookDX = 0;
  lookDY = 0;

  // Virtual keys (fed into InputManager)
  readonly virtualKeys = new Set<string>();
  readonly virtualMouse = new Set<number>();

  private visible = false;
  private enabled = false;

  // Button elements for highlighting
  private btnJump!: HTMLElement;
  private btnMine!: HTMLElement;
  private btnPlace!: HTMLElement;
  private btnInventory!: HTMLElement;
  private btnPause!: HTMLElement;
  private joystickEl!: HTMLElement;
  private joystickKnob!: HTMLElement;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.container = document.createElement('div');
    this.container.id = 'touch-controls';
    this.container.style.display = 'none';
    document.body.appendChild(this.container);

    this.buildUI();
    this.bindEvents();
  }

  static isMobile(): boolean {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
    );
  }

  enable(): void {
    this.enabled = true;
    this.container.style.display = 'block';
    this.visible = true;
  }

  disable(): void {
    this.enabled = false;
    this.container.style.display = 'none';
    this.visible = false;
    this.virtualKeys.clear();
    this.virtualMouse.clear();
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  private buildUI(): void {
    this.container.innerHTML = `
      <div class="touch-joystick" id="touch-joystick">
        <div class="touch-joystick-knob" id="touch-joystick-knob"></div>
      </div>
      <div class="touch-btn touch-btn-jump" id="touch-btn-jump">
        <span>&#x25B2;</span>
      </div>
      <div class="touch-btn touch-btn-mine" id="touch-btn-mine">
        <span>&#x26CF;</span>
      </div>
      <div class="touch-btn touch-btn-place" id="touch-btn-place">
        <span>&#x25A3;</span>
      </div>
      <div class="touch-btn touch-btn-inv" id="touch-btn-inv">
        <span>...</span>
      </div>
      <div class="touch-btn touch-btn-pause" id="touch-btn-pause">
        <span>&#x2759;&#x2759;</span>
      </div>
      <div class="touch-crosshair" id="touch-crosshair"></div>
    `;

    this.joystickEl = this.container.querySelector('#touch-joystick')!;
    this.joystickKnob = this.container.querySelector('#touch-joystick-knob')!;
    this.btnJump = this.container.querySelector('#touch-btn-jump')!;
    this.btnMine = this.container.querySelector('#touch-btn-mine')!;
    this.btnPlace = this.container.querySelector('#touch-btn-place')!;
    this.btnInventory = this.container.querySelector('#touch-btn-inv')!;
    this.btnPause = this.container.querySelector('#touch-btn-pause')!;
  }

  private bindEvents(): void {
    // Prevent default touch behaviors on the whole container
    this.container.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });
    this.container.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });

    // Global touch events on canvas for look control
    this.canvas.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
    this.canvas.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    this.canvas.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: false });
    this.canvas.addEventListener('touchcancel', this.onTouchEnd.bind(this), { passive: false });

    // Joystick touch
    this.joystickEl.addEventListener('touchstart', this.onJoystickStart.bind(this), { passive: false });

    // Button events
    this.bindButton(this.btnJump, 'Space');
    this.bindButton(this.btnMine, null, 0); // left mouse
    this.bindButton(this.btnPlace, null, 2); // right mouse
    this.bindButton(this.btnInventory, 'KeyE');
    this.bindButton(this.btnPause, 'Escape');
  }

  private bindButton(el: HTMLElement, key: string | null, mouseBtn?: number): void {
    el.addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      el.classList.add('active');
      if (key) this.virtualKeys.add(key);
      if (mouseBtn !== undefined) this.virtualMouse.add(mouseBtn);
    }, { passive: false });

    el.addEventListener('touchend', (e) => {
      e.preventDefault();
      e.stopPropagation();
      el.classList.remove('active');
      if (key) this.virtualKeys.delete(key);
      if (mouseBtn !== undefined) this.virtualMouse.delete(mouseBtn);
    }, { passive: false });

    el.addEventListener('touchcancel', (e) => {
      e.preventDefault();
      el.classList.remove('active');
      if (key) this.virtualKeys.delete(key);
      if (mouseBtn !== undefined) this.virtualMouse.delete(mouseBtn);
    }, { passive: false });
  }

  private onJoystickStart(e: TouchEvent): void {
    e.preventDefault();
    e.stopPropagation();
    if (this.joystickId !== null) return;
    const touch = e.changedTouches[0];
    this.joystickId = touch.identifier;
    const rect = this.joystickEl.getBoundingClientRect();
    this.joystickOrigin.x = rect.left + rect.width / 2;
    this.joystickOrigin.y = rect.top + rect.height / 2;
    this.joystickActive = true;
  }

  private onTouchStart(e: TouchEvent): void {
    if (!this.enabled) return;
    e.preventDefault();

    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      const x = touch.clientX;
      const w = window.innerWidth;

      // Right half of screen = look control
      if (x > w * 0.3 && this.lookId === null) {
        // Check it's not on a button
        const el = document.elementFromPoint(touch.clientX, touch.clientY);
        if (el && (el.closest('.touch-btn') || el.closest('.touch-joystick'))) continue;

        this.lookId = touch.identifier;
        this.lookLastPos.x = touch.clientX;
        this.lookLastPos.y = touch.clientY;
      }
    }
  }

  private onTouchMove(e: TouchEvent): void {
    if (!this.enabled) return;
    e.preventDefault();

    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];

      // Joystick move
      if (touch.identifier === this.joystickId) {
        const dx = touch.clientX - this.joystickOrigin.x;
        const dy = touch.clientY - this.joystickOrigin.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const clampedDist = Math.min(dist, this.JOYSTICK_RADIUS);
        const angle = Math.atan2(dy, dx);

        this.joystickPos.x = Math.cos(angle) * clampedDist;
        this.joystickPos.y = Math.sin(angle) * clampedDist;

        // Update knob visual position
        this.joystickKnob.style.transform = `translate(${this.joystickPos.x}px, ${this.joystickPos.y}px)`;

        // Map to WASD keys
        const threshold = 0.3;
        const nx = this.joystickPos.x / this.JOYSTICK_RADIUS;
        const ny = this.joystickPos.y / this.JOYSTICK_RADIUS;

        this.setVirtualKey('KeyW', ny < -threshold);
        this.setVirtualKey('KeyS', ny > threshold);
        this.setVirtualKey('KeyA', nx < -threshold);
        this.setVirtualKey('KeyD', nx > threshold);

        // Sprint if pushed far enough
        const magnitude = clampedDist / this.JOYSTICK_RADIUS;
        this.setVirtualKey('ShiftLeft', magnitude > 0.85);
      }

      // Look drag
      if (touch.identifier === this.lookId) {
        this.lookDX += (touch.clientX - this.lookLastPos.x) * 1.5;
        this.lookDY += (touch.clientY - this.lookLastPos.y) * 1.5;
        this.lookLastPos.x = touch.clientX;
        this.lookLastPos.y = touch.clientY;
      }
    }
  }

  private onTouchEnd(e: TouchEvent): void {
    if (!this.enabled) return;

    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];

      // Joystick release
      if (touch.identifier === this.joystickId) {
        this.joystickId = null;
        this.joystickActive = false;
        this.joystickPos.x = 0;
        this.joystickPos.y = 0;
        this.joystickKnob.style.transform = 'translate(0px, 0px)';
        this.virtualKeys.delete('KeyW');
        this.virtualKeys.delete('KeyS');
        this.virtualKeys.delete('KeyA');
        this.virtualKeys.delete('KeyD');
        this.virtualKeys.delete('ShiftLeft');
      }

      // Look release
      if (touch.identifier === this.lookId) {
        this.lookId = null;
      }
    }
  }

  private setVirtualKey(key: string, active: boolean): void {
    if (active) {
      this.virtualKeys.add(key);
    } else {
      this.virtualKeys.delete(key);
    }
  }

  consumeLookDelta(): [number, number] {
    const dx = this.lookDX;
    const dy = this.lookDY;
    this.lookDX = 0;
    this.lookDY = 0;
    return [dx, dy];
  }

  dispose(): void {
    this.container.remove();
  }
}
